<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import FormsAPI from 'dashboard/api/forms';
import WebsiteLayout from '../components/WebsiteLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import FormBuilder from '../components/FormBuilder.vue';
import TablePagination from '../components/TablePagination.vue';
import ResponsiveTable from '../components/ResponsiveTable.vue';

const { t } = useI18n();

const forms = ref([]);
const loading = ref(false);
const showBuilder = ref(false);
const selectedForm = ref(null);

// Header controls
const searchQuery = ref('');
const showFilterMenu = ref(false);
const showSortMenu = ref(false);
const filterPublished = ref(null);
const filterType = ref(null);
const sortField = ref(null);
const sortOrder = ref('asc');

// Pagination
const currentPage = ref(1);
const itemsPerPage = ref(25);

const headerTitle = computed(() => t('WEBSITE.HEADER.FORMS_TITLE'));

// Filtrage et tri (sans pagination)
const filteredAndSortedForms = computed(() => {
  let result = [...forms.value];

  // Recherche
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    result = result.filter(
      form =>
        form.title?.toLowerCase().includes(query) ||
        form.description?.toLowerCase().includes(query)
    );
  }

  // Filtre publié/brouillon
  if (filterPublished.value !== null) {
    result = result.filter(form => form.published === filterPublished.value);
  }

  // Filtre type (single-step / multi-step)
  if (filterType.value !== null) {
    result = result.filter(form => form.multi_step === filterType.value);
  }

  // Tri
  if (sortField.value) {
    result.sort((a, b) => {
      let aVal = a[sortField.value];
      let bVal = b[sortField.value];

      if (sortField.value === 'created_at') {
        aVal = new Date(aVal || 0);
        bVal = new Date(bVal || 0);
      }

      if (aVal < bVal) return sortOrder.value === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder.value === 'asc' ? 1 : -1;
      return 0;
    });
  }

  return result;
});

// Pagination
const startIndex = computed(() => (currentPage.value - 1) * itemsPerPage.value);

const endIndex = computed(() =>
  Math.min(
    startIndex.value + itemsPerPage.value,
    filteredAndSortedForms.value.length
  )
);

// Résultats paginés (utilisé pour l'affichage)
const paginatedForms = computed(() => {
  return filteredAndSortedForms.value.slice(startIndex.value, endIndex.value);
});

// États
const isEmpty = computed(() => !loading.value && forms.value.length === 0);

const hasNoSearchResults = computed(
  () =>
    !loading.value &&
    forms.value.length > 0 &&
    filteredAndSortedForms.value.length === 0 &&
    (searchQuery.value ||
      filterPublished.value !== null ||
      filterType.value !== null)
);

// Reset pagination when filters change
watch(
  [searchQuery, filterPublished, filterType, sortField, itemsPerPage],
  () => {
    currentPage.value = 1;
  }
);

const fetchForms = async () => {
  loading.value = true;
  try {
    const response = await FormsAPI.get();
    forms.value = response.data;
  } catch (error) {
    useAlert(t('WEBSITE.FORMS.API.ERROR.FETCH_FAILED'));
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  fetchForms();
});

const openCreateForm = () => {
  selectedForm.value = null;
  showBuilder.value = true;
};

const openEditForm = form => {
  selectedForm.value = form;
  showBuilder.value = true;
};

const closeBuilder = () => {
  showBuilder.value = false;
  selectedForm.value = null;
};

const handleSave = async () => {
  await fetchForms();
  closeBuilder();
};

const togglePublish = async form => {
  try {
    if (form.published) {
      await FormsAPI.unpublish(form.id);
      useAlert(t('WEBSITE.FORMS.API.SUCCESS.FORM_UNPUBLISHED'));
    } else {
      await FormsAPI.publish(form.id);
      useAlert(t('WEBSITE.FORMS.API.SUCCESS.FORM_PUBLISHED'));
    }
    await fetchForms();
  } catch (error) {
    const errorMessage =
      error.response?.data?.errors?.[0] ||
      t('WEBSITE.FORMS.API.ERROR.PUBLISH_FAILED');
    useAlert(errorMessage);
  }
};

const deleteForm = async form => {
  // eslint-disable-next-line no-alert, no-restricted-globals
  if (!confirm(t('WEBSITE.FORMS.CONFIRM.DELETE'))) return;

  try {
    await FormsAPI.delete(form.id);
    useAlert(t('WEBSITE.FORMS.API.SUCCESS.FORM_DELETED'));
    await fetchForms();
  } catch (error) {
    useAlert(t('WEBSITE.FORMS.API.ERROR.DELETE_FAILED'));
  }
};

const formatDate = dateString => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

// Fermer tous les menus sauf celui spécifié
const closeAllMenusExcept = exceptMenu => {
  if (exceptMenu !== 'filter') showFilterMenu.value = false;
  if (exceptMenu !== 'sort') showSortMenu.value = false;
};

// Gestion filtres et tri
const handleFilterPublished = value => {
  filterPublished.value = value;
  showFilterMenu.value = false;
};

const handleFilterType = value => {
  filterType.value = value;
  showFilterMenu.value = false;
};

const handleSort = field => {
  if (sortField.value === field) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortField.value = field;
    sortOrder.value = 'asc';
  }
  showSortMenu.value = false;
};

const clearSearch = () => {
  searchQuery.value = '';
  filterPublished.value = null;
  filterType.value = null;
  sortField.value = null;
  sortOrder.value = 'asc';
  currentPage.value = 1;
};
</script>

<template>
  <WebsiteLayout :header-title="headerTitle">
    <template #actions>
      <Button
        :label="t('WEBSITE.FORMS.EMPTY_STATE.BUTTON_LABEL')"
        icon="i-lucide-plus"
        size="sm"
        @click="openCreateForm"
      />
    </template>

    <!-- Loading -->
    <div
      v-if="loading"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>

    <!-- Empty state -->
    <div
      v-else-if="isEmpty"
      class="flex flex-col items-center justify-center py-24 rounded-lg bg-gradient-to-br from-n-solid-1 to-n-slate-2 dark:to-n-slate-1 border-2 border-dashed border-n-weak"
    >
      <div
        class="mb-6 w-20 h-20 rounded-full bg-n-purple-2 dark:bg-n-purple-3 flex items-center justify-center"
      >
        <span class="i-lucide-clipboard-list size-10 text-n-purple-10" />
      </div>
      <h3 class="text-lg font-semibold text-n-slate-12 mb-2">
        {{ t('WEBSITE.FORMS.EMPTY_STATE.TITLE') }}
      </h3>
      <p class="text-sm text-center text-n-slate-10 max-w-md mb-6">
        {{ t('WEBSITE.FORMS.EMPTY_STATE.SUBTITLE') }}
      </p>
      <Button
        :label="t('WEBSITE.FORMS.EMPTY_STATE.BUTTON_LABEL')"
        icon="i-lucide-plus"
        size="sm"
        @click="openCreateForm"
      />
    </div>

    <!-- Header bar avec search, filtres, tri -->
    <div
      v-else-if="!isEmpty"
      class="mb-3 bg-n-solid-1 border border-n-weak/70 rounded-lg shadow-sm"
    >
      <div class="flex items-center gap-2 px-2.5 py-1.5">
        <!-- Search bar -->
        <div class="flex-1 max-w-sm">
          <Input
            v-model="searchQuery"
            type="search"
            :placeholder="t('WEBSITE.FORMS.LIST.SEARCH_PLACEHOLDER')"
            :custom-input-class="[
              'h-8 [&:not(.focus)]:!border-transparent [&:not(.focus)]:!outline-transparent bg-n-alpha-2 dark:bg-n-solid-1 ltr:!pl-8 !py-1 rtl:!pr-8 !text-xs',
            ]"
          >
            <template #prefix>
              <Icon
                icon="i-lucide-search"
                class="absolute -translate-y-1/2 text-n-slate-11 size-3.5 top-1/2 ltr:left-2 rtl:right-2 pointer-events-none"
              />
            </template>
          </Input>
        </div>

        <!-- Séparateur vertical -->
        <div class="h-6 w-px bg-n-weak/40" />

        <!-- Filtres, Tri -->
        <div class="flex items-center gap-1.5">
          <!-- Filtre -->
          <div class="relative">
            <button
              class="px-2.5 py-1.5 bg-n-solid-1 border border-n-weak/50 text-n-slate-11 text-xs hover:bg-n-blue-2 hover:text-n-blue-11 hover:border-n-blue-6 transition-all flex items-center gap-1.5 rounded-lg font-medium"
              :class="{
                'bg-n-blue-3 text-n-blue-11 border-n-blue-6':
                  filterPublished !== null || filterType !== null,
              }"
              @click="
                closeAllMenusExcept('filter');
                showFilterMenu = !showFilterMenu;
              "
            >
              <span class="i-lucide-filter size-3.5" />
              <span>{{ t('WEBSITE.FORMS.LIST.FILTER') }}</span>
              <span
                v-if="filterPublished !== null || filterType !== null"
                class="w-1.5 h-1.5 bg-n-blue-9 rounded-full animate-pulse"
              />
            </button>

            <div
              v-if="showFilterMenu"
              v-on-clickaway="() => (showFilterMenu = false)"
              class="absolute right-0 mt-1.5 w-56 bg-n-solid-1 border border-n-weak/60 shadow-2xl rounded-xl z-[100] py-2.5 overflow-hidden"
            >
              <!-- Status filters -->
              <div
                class="px-3 py-1.5 text-[10px] font-semibold text-n-slate-10 uppercase tracking-wider"
              >
                {{ t('WEBSITE.FORMS.LIST.STATUS') }}
              </div>
              <button
                class="w-full px-4 py-3 text-left text-xs hover:bg-n-alpha-2 transition-all duration-150 flex items-center justify-between group"
                :class="{
                  'bg-n-blue-2 text-n-blue-11':
                    filterPublished === null && filterType === null,
                }"
                @click="
                  handleFilterPublished(null);
                  handleFilterType(null);
                "
              >
                <span class="font-medium">{{
                  t('WEBSITE.FORMS.LIST.ALL_FORMS')
                }}</span>
                <span
                  v-if="filterPublished === null && filterType === null"
                  class="i-lucide-check size-3.5 text-n-blue-11"
                />
              </button>
              <button
                class="w-full px-4 py-3 text-left text-xs hover:bg-n-alpha-2 transition-all duration-150 flex items-center justify-between group"
                :class="{
                  'bg-n-teal-2 text-n-teal-11': filterPublished === true,
                }"
                @click="handleFilterPublished(true)"
              >
                <span class="font-medium flex items-center gap-2.5">
                  <span
                    class="w-2 h-2 rounded-full bg-n-teal-9 group-hover:scale-110 transition-transform"
                  />
                  {{ t('WEBSITE.FORMS.LIST.PUBLISHED_STATUS') }}
                </span>
                <span
                  v-if="filterPublished === true"
                  class="i-lucide-check size-3.5 text-n-teal-11"
                />
              </button>
              <button
                class="w-full px-4 py-3 text-left text-xs hover:bg-n-alpha-2 transition-all duration-150 flex items-center justify-between group"
                :class="{
                  'bg-n-orange-2 text-n-orange-11': filterPublished === false,
                }"
                @click="handleFilterPublished(false)"
              >
                <span class="font-medium flex items-center gap-2.5">
                  <span
                    class="w-2 h-2 rounded-full bg-n-orange-9 group-hover:scale-110 transition-transform"
                  />
                  {{ t('WEBSITE.FORMS.LIST.DRAFT_STATUS') }}
                </span>
                <span
                  v-if="filterPublished === false"
                  class="i-lucide-check size-3.5 text-n-orange-11"
                />
              </button>

              <!-- Type filters -->
              <div class="h-px bg-n-weak/30 my-2 mx-3" />
              <div
                class="px-3 py-1.5 text-[10px] font-semibold text-n-slate-10 uppercase tracking-wider"
              >
                {{ t('WEBSITE.FORMS.LIST.TYPE') }}
              </div>
              <button
                class="w-full px-4 py-3 text-left text-xs hover:bg-n-alpha-2 transition-all duration-150 flex items-center justify-between group"
                :class="{
                  'bg-n-blue-2 text-n-blue-11': filterType === false,
                }"
                @click="handleFilterType(false)"
              >
                <span class="font-medium flex items-center gap-2.5">
                  <span
                    class="i-lucide-file size-3.5 group-hover:scale-110 transition-transform"
                  />
                  {{ t('WEBSITE.FORMS.LIST.SINGLE_STEP') }}
                </span>
                <span
                  v-if="filterType === false"
                  class="i-lucide-check size-3.5 text-n-blue-11"
                />
              </button>
              <button
                class="w-full px-4 py-3 text-left text-xs hover:bg-n-alpha-2 transition-all duration-150 flex items-center justify-between group"
                :class="{
                  'bg-n-purple-2 text-n-purple-11': filterType === true,
                }"
                @click="handleFilterType(true)"
              >
                <span class="font-medium flex items-center gap-2.5">
                  <span
                    class="i-lucide-layers size-3.5 group-hover:scale-110 transition-transform"
                  />
                  {{ t('WEBSITE.FORMS.LIST.MULTI_STEP') }}
                </span>
                <span
                  v-if="filterType === true"
                  class="i-lucide-check size-3.5 text-n-purple-11"
                />
              </button>
            </div>
          </div>

          <!-- Tri -->
          <div class="relative">
            <button
              class="px-2.5 py-1.5 bg-n-solid-1 border border-n-weak/50 text-n-slate-11 text-xs hover:bg-n-blue-2 hover:text-n-blue-11 hover:border-n-blue-6 transition-all flex items-center gap-1.5 rounded-lg font-medium"
              :class="{
                'bg-n-blue-3 text-n-blue-11 border-n-blue-6': sortField,
              }"
              @click="
                closeAllMenusExcept('sort');
                showSortMenu = !showSortMenu;
              "
            >
              <span class="i-lucide-arrow-up-down size-3.5" />
              <span>{{ t('WEBSITE.FORMS.LIST.SORT') }}</span>
              <span
                v-if="sortField"
                class="w-1.5 h-1.5 bg-n-blue-9 rounded-full animate-pulse"
              />
            </button>

            <div
              v-if="showSortMenu"
              v-on-clickaway="() => (showSortMenu = false)"
              class="absolute right-0 mt-1.5 w-60 bg-n-solid-1 border border-n-weak/60 shadow-2xl rounded-xl z-[100] py-2.5 overflow-hidden"
            >
              <button
                class="w-full px-4 py-3 text-left text-xs hover:bg-n-alpha-2 transition-all duration-150 flex items-center justify-between group"
                :class="{
                  'bg-n-blue-2 text-n-blue-11': sortField === 'title',
                }"
                @click="handleSort('title')"
              >
                <span class="flex items-center gap-2.5 font-medium">
                  <span
                    class="i-lucide-type size-3.5 group-hover:scale-110 transition-transform"
                  />
                  <span>{{ t('WEBSITE.FORMS.LIST.SORT_TITLE') }}</span>
                </span>
                <span
                  v-if="sortField === 'title'"
                  :class="
                    sortOrder === 'asc'
                      ? 'i-lucide-arrow-up'
                      : 'i-lucide-arrow-down'
                  "
                  class="size-3.5 text-n-blue-11"
                />
              </button>
              <button
                class="w-full px-4 py-3 text-left text-xs hover:bg-n-alpha-2 transition-all duration-150 flex items-center justify-between group"
                :class="{
                  'bg-n-blue-2 text-n-blue-11': sortField === 'created_at',
                }"
                @click="handleSort('created_at')"
              >
                <span class="flex items-center gap-2.5 font-medium">
                  <span
                    class="i-lucide-calendar size-3.5 group-hover:scale-110 transition-transform"
                  />
                  <span>{{ t('WEBSITE.FORMS.LIST.SORT_DATE') }}</span>
                </span>
                <span
                  v-if="sortField === 'created_at'"
                  :class="
                    sortOrder === 'asc'
                      ? 'i-lucide-arrow-up'
                      : 'i-lucide-arrow-down'
                  "
                  class="size-3.5 text-n-blue-11"
                />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- No search results -->
    <div
      v-if="hasNoSearchResults"
      class="flex flex-col items-center justify-center py-16 bg-n-solid-1 border border-n-weak/70 rounded-lg"
    >
      <span class="i-lucide-search-x mb-4 text-n-slate-9 size-12" />
      <h3 class="text-base font-semibold text-center text-n-slate-12 mb-2">
        {{ t('WEBSITE.FORMS.NO_RESULTS.TITLE') }}
      </h3>
      <p class="text-sm text-center text-n-slate-10 max-w-md mb-6">
        {{ t('WEBSITE.FORMS.NO_RESULTS.SUBTITLE') }}
      </p>
      <Button
        :label="t('WEBSITE.FORMS.NO_RESULTS.CLEAR_BUTTON')"
        icon="i-lucide-x"
        size="sm"
        variant="outline"
        color="slate"
        @click="clearSearch"
      />
    </div>

    <!-- Forms list -->
    <div
      v-else-if="!isEmpty && !hasNoSearchResults"
      class="overflow-hidden border rounded-lg border-n-weak/70 bg-n-solid-1 shadow-sm min-h-[400px]"
    >
      <ResponsiveTable>
        <table class="w-full">
          <thead class="bg-n-alpha-1/50 border-b border-n-weak/60">
            <tr>
              <th
                class="px-5 py-3 text-xs font-semibold text-left tracking-wide text-n-slate-11"
              >
                <div class="flex items-center gap-1.5">
                  <span class="i-lucide-clipboard-list size-3.5 opacity-40" />
                  <span>{{ t('WEBSITE.FORMS.LIST.TITLE') }}</span>
                </div>
              </th>
              <th
                class="px-5 py-3 text-xs font-semibold text-left tracking-wide text-n-slate-11"
              >
                <div class="flex items-center gap-1.5">
                  <span class="i-lucide-layers size-3.5 opacity-40" />
                  <span>{{ t('WEBSITE.FORMS.LIST.TYPE') }}</span>
                </div>
              </th>
              <th
                class="px-5 py-3 text-xs font-semibold text-left tracking-wide text-n-slate-11"
              >
                <div class="flex items-center gap-1.5">
                  <span class="i-lucide-list-checks size-3.5 opacity-40" />
                  <span>{{ t('WEBSITE.FORMS.LIST.STEPS') }}</span>
                </div>
              </th>
              <th
                class="px-5 py-3 text-xs font-semibold text-left tracking-wide text-n-slate-11"
              >
                <div class="flex items-center gap-1.5">
                  <span class="i-lucide-activity size-3.5 opacity-40" />
                  <span>{{ t('WEBSITE.FORMS.LIST.STATUS') }}</span>
                </div>
              </th>
              <th
                class="px-5 py-3 text-xs font-semibold text-left tracking-wide text-n-slate-11"
              >
                <div class="flex items-center gap-1.5">
                  <span class="i-lucide-calendar size-3.5 opacity-40" />
                  <span>{{ t('WEBSITE.FORMS.LIST.CREATED') }}</span>
                </div>
              </th>
              <th
                class="sticky right-0 px-5 py-3 text-xs font-semibold text-right tracking-wide text-n-slate-11 bg-n-alpha-1/50 backdrop-blur-sm shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.1)]"
              >
                <div class="flex items-center justify-end gap-1.5">
                  <span>{{ t('WEBSITE.FORMS.LIST.ACTIONS') }}</span>
                  <span class="i-lucide-more-horizontal size-3.5 opacity-30" />
                </div>
              </th>
            </tr>
          </thead>
          <tbody class="bg-n-solid-1">
            <tr
              v-for="form in paginatedForms"
              :key="form.id"
              class="transition-all duration-150 ease-out border-t border-n-weak/40 hover:bg-n-blue-2/50 group"
            >
              <td class="px-5 py-3">
                <div class="min-w-0">
                  <span
                    class="text-sm font-medium text-n-slate-12 block line-clamp-1"
                  >
                    {{ form.title }}
                  </span>
                  <span class="text-xs text-n-slate-10 line-clamp-1">
                    {{ form.description || '-' }}
                  </span>
                </div>
              </td>
              <td class="px-5 py-3">
                <span
                  class="inline-flex items-center px-2 py-1 text-xs font-medium rounded"
                  :class="[
                    form.multi_step
                      ? 'bg-n-purple-2 text-n-purple-11'
                      : 'bg-n-blue-2 text-n-blue-11',
                  ]"
                >
                  {{
                    form.multi_step
                      ? t('WEBSITE.FORMS.LIST.MULTI_STEP')
                      : t('WEBSITE.FORMS.LIST.SINGLE_STEP')
                  }}
                </span>
              </td>
              <td class="px-5 py-3 text-sm text-n-slate-10">
                {{ form.steps_count || 0 }}
                {{
                  form.steps_count > 1
                    ? t('WEBSITE.FORMS.LIST.STEPS_PLURAL')
                    : t('WEBSITE.FORMS.LIST.STEPS_SINGULAR')
                }}
              </td>
              <td class="px-5 py-3">
                <div
                  class="inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] font-medium rounded-full border whitespace-nowrap transition-all duration-200"
                  :class="[
                    form.published
                      ? 'bg-n-teal-2 text-n-teal-11 border-n-teal-6'
                      : 'bg-n-orange-2 text-n-orange-11 border-n-orange-6',
                  ]"
                >
                  <div
                    class="w-1.5 h-1.5 rounded-full"
                    :class="[
                      form.published
                        ? 'bg-n-teal-9 animate-pulse'
                        : 'bg-n-orange-9',
                    ]"
                  />
                  {{
                    form.published
                      ? t('WEBSITE.FORMS.LIST.PUBLISHED_STATUS')
                      : t('WEBSITE.FORMS.LIST.DRAFT_STATUS')
                  }}
                </div>
              </td>
              <td class="px-5 py-3 text-sm text-n-slate-10">
                {{ formatDate(form.created_at) }}
              </td>
              <td
                class="sticky right-0 px-5 py-3 bg-n-solid-1 backdrop-blur-sm shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.1)]"
              >
                <div class="flex justify-end gap-2">
                  <button
                    class="flex items-center justify-center w-8 h-8 rounded-md transition-all duration-200 shadow-sm hover:shadow-md border"
                    :class="[
                      form.published
                        ? 'bg-n-orange-3 hover:bg-n-orange-4 text-n-orange-11 hover:text-n-orange-12 border-n-orange-6 hover:border-n-orange-7'
                        : 'bg-n-teal-3 hover:bg-n-teal-4 text-n-teal-11 hover:text-n-teal-12 border-n-teal-6 hover:border-n-teal-7',
                    ]"
                    :title="
                      form.published
                        ? t('WEBSITE.FORMS.FORM.UNPUBLISH')
                        : t('WEBSITE.FORMS.FORM.PUBLISH')
                    "
                    @click="togglePublish(form)"
                  >
                    <span
                      :class="[
                        form.published ? 'i-lucide-eye-off' : 'i-lucide-eye',
                      ]"
                      class="size-4"
                    />
                  </button>
                  <button
                    class="flex items-center justify-center w-8 h-8 rounded-md bg-n-blue-3 hover:bg-n-blue-4 text-n-blue-11 hover:text-n-blue-12 border border-n-blue-6 hover:border-n-blue-7 transition-all duration-200 shadow-sm hover:shadow-md"
                    :title="t('WEBSITE.FORMS.FORM.EDIT')"
                    @click="openEditForm(form)"
                  >
                    <span class="i-lucide-edit size-4" />
                  </button>
                  <button
                    class="flex items-center justify-center w-8 h-8 rounded-md bg-n-ruby-3 hover:bg-n-ruby-4 text-n-ruby-11 hover:text-n-ruby-12 border border-n-ruby-6 hover:border-n-ruby-7 transition-all duration-200 shadow-sm hover:shadow-md"
                    :title="t('WEBSITE.FORMS.FORM.DELETE')"
                    @click="deleteForm(form)"
                  >
                    <span class="i-lucide-trash-2 size-4" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </ResponsiveTable>
    </div>

    <!-- Pagination Footer -->
    <div
      v-if="
        !isEmpty && !hasNoSearchResults && filteredAndSortedForms.length > 0
      "
      class="mt-6"
    >
      <TablePagination
        :current-page="currentPage"
        :total-items="filteredAndSortedForms.length"
        :items-per-page="itemsPerPage"
        @update:current-page="currentPage = $event"
        @update:items-per-page="itemsPerPage = $event"
      />
    </div>

    <!-- Form Builder Drawer -->
    <FormBuilder
      v-if="showBuilder"
      :form="selectedForm"
      @close="closeBuilder"
      @save="handleSave"
    />
  </WebsiteLayout>
</template>
